import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { SortableTable, type SortableColumn } from '@/components/ui/SortableTable';
import { fetchSeoProjectOptions } from '@/data/sources';
import { fetchSeoKeywordResearch } from '@/data/sources-seob';
import type { KeywordIdea, KeywordResearchResult } from '@/types/seob';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { resolveActiveProjectId } from '@/pages/seo/domain-scope';
import { DomainChoiceGate } from '@/pages/seo/DomainChoiceGate';

/* /seo/keyword-research — Keyword Research (W2a). Read-only, source-attributed
 * keyword ideas for a seed term, from DataForSEO Labs keyword_ideas.
 *
 * HONESTY + SAFETY INVARIANTS (W2a contract):
 *  - PAID (DataForSEO Labs): NEVER auto-fires. Project options is a free list
 *    read; the keyword ideas run ONLY on an explicit click. No useQuery is wired
 *    to the paid read, so mounting this page charges nothing.
 *  - Source-attributed (source + provider fetched_at + cache indicator); every
 *    null metric renders as an em-dash — numbers are NEVER invented.
 *  - Results render in the shared SortableTable (client-side sort only — sorting
 *    is free and never triggers a reload).
 *  - Honest states: idle / loading / error / not-configured (503).
 */

const ROW_LIMIT = 50;

function sourceLabel(source: string): string {
  if (source.startsWith('dataforseo.labs')) return 'DataForSEO Labs';
  if (source.startsWith('dataforseo')) return 'DataForSEO';
  return source;
}

function formatInt(n: number): string {
  return n.toLocaleString('en-GB');
}

/** em-dash for any null provider metric — never a fabricated value. */
function orInt(v: number | null): string {
  return v === null ? '—' : formatInt(v);
}
function orMoney(v: number | null): string {
  return v === null ? '—' : `£${v.toFixed(2)}`;
}
function orRatio(v: number | null): string {
  return v === null ? '—' : v.toFixed(2);
}

interface Panel {
  title: string;
  body: string;
}

type SectionState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; panel: Panel }
  | { status: 'done'; data: T };

function describeError(err: unknown): Panel {
  if (err instanceof ApiError) {
    if (err.status === 503 || err.code === 'provider_unavailable') {
      return {
        title: 'Keyword research isn’t connected yet',
        body: 'Live keyword ideas need the DataForSEO Labs connection, which isn’t configured for this environment yet. Nothing was fetched and nothing was charged — an honest “not configured” state, never placeholder data.',
      };
    }
    if (err.status === 402 || err.code === 'budget_exceeded') {
      return {
        title: 'Monthly provider budget reached',
        body: 'This lookup would exceed your workspace’s monthly provider budget, so it was refused before any provider call was made — nothing was spent. It resumes when the budget resets, or after you raise the cap on Spend & Budget.',
      };
    }
    if (err.status === 429 || err.code === 'rate_limited') {
      return {
        title: 'Provider rate limit reached',
        body: 'This workspace has hit the provider rate limit, so nothing was fetched or charged. Try again as the window rolls over.',
      };
    }
    if (err.status === 502 || err.code === 'provider_error') {
      return {
        title: 'The provider request failed',
        body: `${err.message ? `${err.message} ` : ''}Nothing was stored or charged — try again in a moment.`,
      };
    }
    if (err.status === 404) {
      return {
        title: 'Project not found',
        body: 'This SEO project isn’t available to your workspace. Pick another project and try again.',
      };
    }
    return {
      title: 'The lookup could not be completed',
      body: err.message || 'The data service returned an unexpected response. Nothing was charged — try again.',
    };
  }
  return {
    title: 'The lookup could not be completed',
    body: err instanceof Error ? err.message : 'Unexpected error — nothing was charged. Try again.',
  };
}

function SourceFooter({
  source,
  fetchedAt,
  cacheHit,
}: {
  source: string;
  fetchedAt: string | null;
  cacheHit: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
      <Badge tone="neutral">Source: {sourceLabel(source)}</Badge>
      <Badge tone={cacheHit ? 'neutral' : 'success'}>
        {cacheHit ? 'Served from cache' : 'Live result'}
      </Badge>
      <span>{fetchedAt ? `Last updated ${fetchedAt}` : 'Update time not reported'}</span>
    </div>
  );
}

const NUM_CELL = 'font-mono text-text-secondary';

const KEYWORD_COLUMNS: SortableColumn<KeywordIdea>[] = [
  {
    key: 'keyword',
    header: 'Keyword',
    render: (k) => k.keyword,
    truncate: true,
    maxWidthClass: 'max-w-[280px]',
    title: (k) => k.keyword,
    cellClassName: 'font-medium text-text-primary',
  },
  {
    key: 'volume',
    header: 'Volume',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (k) => k.volume,
    render: (k) => orInt(k.volume),
    cellClassName: `${NUM_CELL} text-text-primary`,
  },
  {
    key: 'cpc',
    header: 'CPC',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (k) => k.cpc,
    render: (k) => orMoney(k.cpc),
    cellClassName: NUM_CELL,
  },
  {
    key: 'competition',
    header: 'Competition',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (k) => k.competition,
    render: (k) => orRatio(k.competition),
    cellClassName: NUM_CELL,
  },
  {
    key: 'difficulty',
    header: 'Difficulty',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (k) => k.difficulty,
    render: (k) => orInt(k.difficulty),
    cellClassName: NUM_CELL,
  },
];

export function SeoKeywordResearchPage() {
  const { activeWorkspace } = useAuth();
  const canManage = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const navigate = useNavigate();

  // Free list read — safe on mount. The paid keyword ideas are never wired to a
  // query; they run on click only.
  const projectsQ = useQuery({
    queryKey: ['seo', 'project-options'],
    queryFn: fetchSeoProjectOptions,
  });
  const projects = projectsQ.data ?? [];
  const { active, select } = useActiveDomain();

  const projectId = resolveActiveProjectId(active, projects);

  const [seed, setSeed] = useState('');
  const [research, setResearch] = useState<SectionState<KeywordResearchResult>>({ status: 'idle' });

  const runResearch = async () => {
    if (projectId === null || research.status === 'loading') return;
    const trimmed = seed.trim();
    if (trimmed === '') return;
    setResearch({ status: 'loading' });
    try {
      const data = await fetchSeoKeywordResearch(projectId, trimmed, ROW_LIMIT);
      setResearch({ status: 'done', data });
    } catch (err) {
      setResearch({ status: 'error', panel: describeError(err) });
    }
  };

  const onProjectChange = (id: string) => {
    void select(Number(id));
    setResearch({ status: 'idle' });
  };

  if (projectsQ.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Keyword Research" />
        <LoadingState rows={4} />
      </div>
    );
  }
  if (projectsQ.error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Keyword Research" />
        <ErrorState error={projectsQ.error} onRetry={() => void projectsQ.refetch()} />
      </div>
    );
  }
  if (projects.length > 0 && projectId === null) {
    return <DomainChoiceGate options={projects} onSelect={(id) => void select(Number(id))} />;
  }
  if (projects.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Keyword Research" />
        <EmptyState
          title="No project to explore yet"
          teach="Keyword Research runs against an SEO project. Add your first domain on the Projects page, then come back here to expand a seed keyword into ranked ideas."
          action={
            <Button variant="primary" onClick={() => navigate('/seo/projects')}>
              Go to Projects
            </Button>
          }
        />
      </div>
    );
  }

  const inputClass =
    'h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

  const projectSelector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Domain</span>
      <select
        value={projectId ?? ''}
        onChange={(e) => onProjectChange(e.target.value)}
        className={inputClass}
        aria-label="SEO project"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Keyword Research"
        subtitle="Expand a seed keyword into ranked ideas from licensed DataForSEO Labs data — loaded on demand, source-attributed, and honest about every missing metric."
        actions={projectSelector}
      />

      {!canManage && (
        <div role="status" className="rounded-md border border-border-subtle bg-inset/50 px-3 py-2 text-sm text-text-secondary">
          Keyword research uses paid provider data, so it’s limited to workspace owners and operators.
        </div>
      )}

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="engine">DataForSEO Labs</Badge>
          <span className="text-sm font-medium text-text-primary">Keyword ideas</span>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-text-tertiary">
              Paid · budget-guarded · 7-day cache · top {ROW_LIMIT}
            </span>
          </div>
        </div>
        <p className="mt-1 text-sm text-text-secondary">
          Enter a seed keyword to see related ideas with search volume, CPC, competition and
          difficulty. Runs only when you press the button — nothing is fetched or charged on load.
        </p>

        <form
          className="mt-3 flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void runResearch();
          }}
        >
          <input
            type="text"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            placeholder="e.g. community growth platform"
            aria-label="Seed keyword"
            className={`${inputClass} min-w-[16rem] flex-1`}
            disabled={!canManage}
          />
          <Button
            type="submit"
            variant="primary"
            disabled={!canManage || research.status === 'loading' || seed.trim() === ''}
            title={canManage ? undefined : 'Owner/operator only — this uses paid provider data'}
          >
            {research.status === 'loading'
              ? 'Loading…'
              : research.status === 'done'
                ? 'Refresh'
                : 'Get keyword ideas'}
          </Button>
        </form>

        <div className="mt-4">
          {research.status === 'idle' && (
            <p className="text-sm text-text-tertiary">
              No data yet — enter a seed keyword and load the ideas.
            </p>
          )}
          {research.status === 'loading' && <LoadingState rows={4} />}
          {research.status === 'error' && (
            <div role="status" className="rounded-md border border-border-default bg-surface px-4 py-3">
              <div className="text-sm font-medium text-text-primary">{research.panel.title}</div>
              <p className="mt-1 text-sm text-text-secondary">{research.panel.body}</p>
            </div>
          )}
          {research.status === 'done' &&
            (research.data.keywords.length === 0 ? (
              <div className="rounded-md border border-border-subtle bg-canvas p-3">
                <p className="text-sm text-text-secondary">
                  The provider returned no keyword ideas for this seed — an honest empty result, not
                  an error.
                </p>
                <div className="mt-2">
                  <SourceFooter
                    source={research.data.source}
                    fetchedAt={research.data.fetched_at}
                    cacheHit={research.data.cache_hit}
                  />
                </div>
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
                <SortableTable
                  rows={research.data.keywords}
                  columns={KEYWORD_COLUMNS}
                  getRowKey={(k, i) => `${k.keyword}-${i}`}
                  minWidthClass="min-w-[640px]"
                  caption="Keyword ideas with volume, CPC, competition and difficulty"
                />
                <SourceFooter
                  source={research.data.source}
                  fetchedAt={research.data.fetched_at}
                  cacheHit={research.data.cache_hit}
                />
              </div>
            ))}
        </div>
      </Card>
    </div>
  );
}
